import { Box } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { forwardRef, useEffect, useState } from 'react';

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  40% { transform: translateY(-8px); }
  60% { transform: translateY(-4px); }
`;

interface GoogleScrollButtonProps {
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  isAtBottom: boolean;
  bounceCount: number;
  isLightBg?: boolean;
}

const GoogleScrollButton = forwardRef<HTMLDivElement, GoogleScrollButtonProps>(({
  scrollContainerRef,
  isAtBottom,
  bounceCount,
  isLightBg = false,
}, ref) => {
  const [shouldBounce, setShouldBounce] = useState(false);
  const [iterations, setIterations] = useState(0);

  useEffect(() => {
    if (bounceCount <= 0) return;

    const count = bounceCount <= 3 ? bounceCount : 1;
    setIterations(count);
    const startTimer = setTimeout(() => {
      setShouldBounce(true);
    }, 300);
    const stopTimer = setTimeout(() => {
      setShouldBounce(false);
    }, 300 + count * 600);
    return () => {
      clearTimeout(startTimer);
      clearTimeout(stopTimer);
    };
  }, [bounceCount]);

  const handleClick = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = container.scrollHeight * 0.75;
    const remaining = container.scrollHeight - container.scrollTop - container.clientHeight;

    container.scrollTo({
      top:
        remaining <= scrollAmount
          ? container.scrollHeight
          : container.scrollTop + scrollAmount,
      behavior: 'smooth',
    });
  };

  return (
    <Box
      ref={ref}
      position="relative"
      mt="-90px"
      mx="auto"
      w="fit-content"
      zIndex={5}
      role="button"
      aria-label="Scroll down"
      tabIndex={isAtBottom ? -1 : 0}
      cursor={isAtBottom ? 'default' : 'pointer'}
      onClick={isAtBottom ? undefined : handleClick}
      opacity={isAtBottom ? 0 : 1}
      pointerEvents={isAtBottom ? 'none' : 'auto'}
      transition="opacity 0.2s ease"
      animation={
        shouldBounce
          ? `${bounce} 0.6s ease-out ${iterations} both`
          : undefined
      }
    >
      <Box
        w="48px"
        h="48px"
        borderRadius="full"
        bg={isLightBg ? '#1D2B36' : '#F8F8F8'}
        boxShadow={isLightBg
          ? '0px 5px 10px rgba(0,0,0,0.2), 0px 15px 30px rgba(0,0,0,0.2)'
          : '0px 5px 10px rgba(0,0,0,0.1), 0px 15px 30px rgba(0,0,0,0.1), 0px 20px 40px rgba(0,0,0,0.15)'
        }
        backdropFilter="blur(10px)"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <svg
          width="27"
          height="20"
          viewBox="47 31 29 22"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M59.6591 47.3408C58.6423 47.3408 57.8182 48.1649 57.8182 49.1817C57.8182 50.1985 58.6423 51.0226 59.6591 51.0226H65.7955C70.8788 51.0226 75 46.9014 75 41.818C75 36.7347 70.8788 32.6135 65.7955 32.6135H60.8864C59.8696 32.6135 59.0455 33.4376 59.0455 34.4544C59.0455 35.4712 59.8696 36.2953 60.8864 36.2953H59.6591C58.6423 36.2953 57.8182 37.1194 57.8182 38.1362C57.8182 39.153 58.6423 39.9771 59.6591 39.9771H58.4318C57.415 39.9771 56.5909 40.8012 56.5909 41.818C56.5909 42.8348 57.415 43.6589 58.4318 43.6589H49.8409C48.8241 43.6589 48 44.483 48 45.4998C48 46.5166 48.8241 47.3408 49.8409 47.3408H63.9545H59.6591Z"
            stroke={isLightBg ? '#F8F8F8' : '#1D2B36'}
            strokeWidth="1.5"
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M51.0682 32.0012V40.5921"
            stroke={isLightBg ? '#F8F8F8' : '#1D2B36'}
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M49.2272 33.8411L51.0681 32.0002L52.909 33.8411"
            stroke={isLightBg ? '#F8F8F8' : '#1D2B36'}
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M49.2272 38.7512L51.0681 40.5921L52.909 38.7512"
            stroke={isLightBg ? '#F8F8F8' : '#1D2B36'}
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Box>
    </Box>
  );
});

GoogleScrollButton.displayName = 'GoogleScrollButton';

export default GoogleScrollButton;
